import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  MenuItem,
  Button,
  Chip,
  CircularProgress,
  Card,
  CardContent,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  LinearProgress,
  Collapse,
  Autocomplete,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  FileDownload as ExcelIcon,
  PictureAsPdf as PdfIcon,
  Print as PrintIcon,
  TrendingUp,
  Speed,
  LocalShipping,
  Category as CategoryIcon,
  KeyboardArrowDown,
  KeyboardArrowUp,
} from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { StockIssue } from '../../types/stockIssues.types';
import { stockIssuesService } from '../../services/stockIssues.service';
import { Warehouse } from '../../types/warehouses.types';
import { warehousesService } from '../../services/warehouses.service';
import { Branch } from '../../types/branches.types';
import { branchesService } from '../../services/branches.service';
import { itemsService } from '../../services/items.service';
import { Item } from '../../types/items.types';
import { companyInfoService, CompanyInfo } from '../../services/companyInfo.service';
import { toast } from 'react-toastify';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const translations = {
  en: {
    title: 'Fast Moving Items Report',
    titleAr: 'تقرير الأصناف سريعة الحركة',
    top20: 'Top 20 Items',
    byCategory: 'By Category',
    byWarehouse: 'By Warehouse',
    byBranch: 'By Branch',
    movementAnalysis: 'Movement Analysis',
    period: 'Period',
    lastMonth: 'Last Month',
    last3Months: 'Last 3 Months',
    last6Months: 'Last 6 Months',
    lastYear: 'Last Year',
    warehouse: 'Warehouse',
    branch: 'Branch',
    allWarehouses: 'All Warehouses',
    allBranches: 'All Branches',
    itemCode: 'Item Code',
    itemName: 'Item',
    categoryName: 'Category',
    warehouseName: 'Warehouse',
    branchName: 'Branch',
    totalMovement: 'Total Movement',
    movementQty: 'Movement Qty',
    movementValue: 'Movement Value',
    avgMovement: 'Avg Daily Movement',
    movementRate: 'Daily Rate',
    totalItems: 'Total Items',
    fastestItem: 'Fastest Item',
    refresh: 'Refresh',
    exportExcel: 'Export Excel',
    exportPdf: 'Export PDF',
    print: 'Print',
    noData: 'No movement data',
    loading: 'Loading...',
    kwd: 'KWD',
    asOf: 'As of',
    filters: 'Filters',
    itemsCount: 'Items',
    unitsPerDay: 'units/day',
    itemDetails: 'Item Details',
    showDetails: 'Show Details',
    hideDetails: 'Hide Details',
  },
  ar: {
    title: 'تقرير الأصناف سريعة الحركة',
    titleAr: 'تقرير الأصناف سريعة الحركة',
    top20: 'أكثر 20 صنف حركة',
    byCategory: 'حسب الفئة',
    byWarehouse: 'حسب المخزن',
    byBranch: 'حسب الفرع',
    movementAnalysis: 'تحليل الحركة',
    period: 'الفترة',
    lastMonth: 'آخر شهر',
    last3Months: 'آخر 3 أشهر',
    last6Months: 'آخر 6 أشهر',
    lastYear: 'آخر سنة',
    warehouse: 'المخزن',
    branch: 'الفرع',
    allWarehouses: 'جميع المخازن',
    allBranches: 'جميع الفروع',
    itemCode: 'كود الصنف',
    itemName: 'الصنف',
    categoryName: 'الفئة',
    warehouseName: 'المخزن',
    branchName: 'الفرع',
    totalMovement: 'إجمالي الحركة',
    movementQty: 'كمية الحركة',
    movementValue: 'قيمة الحركة',
    avgMovement: 'متوسط الحركة اليومية',
    movementRate: 'المعدل اليومي',
    totalItems: 'إجمالي الأصناف',
    fastestItem: 'أسرع صنف',
    refresh: 'تحديث',
    exportExcel: 'تصدير Excel',
    exportPdf: 'تصدير PDF',
    print: 'طباعة',
    noData: 'لا توجد بيانات حركة',
    loading: 'جاري التحميل...',
    kwd: 'د.ك',
    asOf: 'كما في',
    filters: 'الفلاتر',
    itemsCount: 'صنف',
    unitsPerDay: 'وحدة/يوم',
    itemDetails: 'تفاصيل الأصناف',
    showDetails: 'عرض التفاصيل',
    hideDetails: 'إخفاء التفاصيل',
  },
};

interface FastMovingItem {
  itemId: number;
  itemCode: string;
  itemName: string;
  itemNameAr: string;
  categoryId?: number;
  categoryName: string;
  categoryNameAr: string;
  warehouseId?: number;
  warehouseName: string;
  warehouseNameAr: string;
  branchId?: number;
  branchName: string;
  branchNameAr: string;
  totalQuantity: number;
  totalValue: number;
  avgDailyMovement: number;
  movementRate: number;
}

interface CategorySummary {
  categoryId?: number;
  categoryName: string;
  categoryNameAr: string;
  itemsCount: number;
  totalQuantity: number;
  totalValue: number;
  avgMovement: number;
}

interface LocationSummary {
  locationId?: number;
  locationName: string;
  locationNameAr: string;
  itemsCount: number;
  totalQuantity: number;
  totalValue: number;
  avgMovement: number;
}

const FastMovingReport: React.FC = () => {
  const { language } = useLanguage();
  const t = translations[language];
  const isRTL = language === 'ar';

  const [issues, setIssues] = useState<StockIssue[]>([]);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [currentTab, setCurrentTab] = useState(0);
  
  const [warehouseFilter, setWarehouseFilter] = useState<number | ''>('');
  const [branchFilter, setBranchFilter] = useState<number | ''>('');
  const [periodFilter, setPeriodFilter] = useState<number>(30);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  useEffect(() => {
    loadWarehouses();
    loadBranches();
    loadCompanyInfo();
    loadItems();
    loadData();
  }, []);

  useEffect(() => {
    if (warehouseFilter || branchFilter || periodFilter) {
      loadData();
    }
  }, [warehouseFilter, branchFilter, periodFilter]);

  const loadCompanyInfo = async () => {
    try {
      const info = await companyInfoService.getCompanyInfo();
      setCompanyInfo(info);
    } catch (error) {
      console.error('Failed to load company info:', error);
    }
  };

  const loadWarehouses = async () => {
    try {
      const data = await warehousesService.getAll();
      setWarehouses(data);
    } catch (error) {
      console.error('Failed to load warehouses:', error);
    }
  };

  const loadBranches = async () => {
    try {
      const data = await branchesService.getAll();
      setBranches(data);
    } catch (error) {
      console.error('Failed to load branches:', error);
    }
  };

  const loadItems = async () => {
    try {
      const data = await itemsService.getAll();
      setItems(data);
    } catch (error) {
      console.error('Failed to load items:', error);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await stockIssuesService.getAll();
      
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - periodFilter);
      
      const filtered = data.filter(issue => {
        const issueDate = new Date(issue.issueDate);
        const dateMatch = issueDate >= cutoffDate;
        const statusMatch = issue.status === 'Posted';
        const warehouseMatch = !warehouseFilter || issue.warehouseId === warehouseFilter;
        const branchMatch = !branchFilter || issue.branchId === branchFilter;
        
        return dateMatch && statusMatch && warehouseMatch && branchMatch;
      });
      
      setIssues(filtered);
    } catch (error) {
      console.error('Failed to load issues:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFastMovingItems = (): FastMovingItem[] => {
    const itemMap = new Map<number, FastMovingItem>();
    const itemsLookup = new Map(items.map(i => [i.itemId, i]));

    issues.forEach(issue => {
      issue.details?.forEach(detail => {
        if (!itemMap.has(detail.itemId)) {
          const itemInfo = itemsLookup.get(detail.itemId);
          
          itemMap.set(detail.itemId, {
            itemId: detail.itemId,
            itemCode: detail.itemCode || '',
            itemName: detail.itemName || '',
            itemNameAr: detail.itemNameAr || '',
            categoryId: itemInfo?.categoryId,
            categoryName: itemInfo?.categoryName || 'عام',
            categoryNameAr: itemInfo?.categoryNameAr || 'عام',
            warehouseId: issue.warehouseId,
            warehouseName: isRTL ? (issue.warehouseNameAr || issue.warehouseName || '-') : (issue.warehouseName || '-'),
            warehouseNameAr: issue.warehouseNameAr || '-',
            branchId: issue.branchId,
            branchName: isRTL ? (issue.branchNameAr || issue.branchName || '-') : (issue.branchName || '-'),
            branchNameAr: issue.branchNameAr || '-',
            totalQuantity: 0,
            totalValue: 0,
            avgDailyMovement: 0,
            movementRate: 0,
          });
        }

        const item = itemMap.get(detail.itemId)!;
        item.totalQuantity += detail.quantity;
        item.totalValue += detail.totalCost;
      });
    });

    itemMap.forEach(item => {
      item.avgDailyMovement = item.totalQuantity / periodFilter;
      item.movementRate = item.avgDailyMovement;
    });

    return Array.from(itemMap.values()).sort((a, b) => b.totalQuantity - a.totalQuantity);
  };

  const getTop20Items = (): FastMovingItem[] => {
    return getFastMovingItems().slice(0, 20);
  };

  const getCategorySummary = (): CategorySummary[] => {
    const summary = new Map<number | undefined, CategorySummary>();
    const items = getFastMovingItems();

    items.forEach(item => {
      const key = item.categoryId;
      if (!summary.has(key)) {
        summary.set(key, {
          categoryId: item.categoryId,
          categoryName: item.categoryName,
          categoryNameAr: item.categoryNameAr,
          itemsCount: 0,
          totalQuantity: 0,
          totalValue: 0,
          avgMovement: 0,
        });
      }

      const cat = summary.get(key)!;
      cat.itemsCount++;
      cat.totalQuantity += item.totalQuantity;
      cat.totalValue += item.totalValue;
    });

    summary.forEach(cat => {
      cat.avgMovement = cat.totalQuantity / periodFilter;
    });

    return Array.from(summary.values()).sort((a, b) => b.totalQuantity - a.totalQuantity);
  };

  const getWarehouseSummary = (): LocationSummary[] => {
    const summary = new Map<number, LocationSummary>();
    const items = getFastMovingItems();

    items.forEach(item => {
      if (item.warehouseId) {
        if (!summary.has(item.warehouseId)) {
          summary.set(item.warehouseId, {
            locationId: item.warehouseId,
            locationName: item.warehouseName,
            locationNameAr: item.warehouseNameAr,
            itemsCount: 0,
            totalQuantity: 0,
            totalValue: 0,
            avgMovement: 0,
          });
        }

        const loc = summary.get(item.warehouseId)!;
        loc.itemsCount++;
        loc.totalQuantity += item.totalQuantity;
        loc.totalValue += item.totalValue;
      }
    });

    summary.forEach(loc => {
      loc.avgMovement = loc.totalQuantity / periodFilter;
    });

    return Array.from(summary.values()).sort((a, b) => b.totalQuantity - a.totalQuantity);
  };

  const getBranchSummary = (): LocationSummary[] => {
    const summary = new Map<number, LocationSummary>();
    const items = getFastMovingItems();

    items.forEach(item => {
      if (item.branchId) {
        if (!summary.has(item.branchId)) {
          summary.set(item.branchId, {
            locationId: item.branchId,
            locationName: item.branchName,
            locationNameAr: item.branchNameAr,
            itemsCount: 0,
            totalQuantity: 0,
            totalValue: 0,
            avgMovement: 0,
          });
        }

        const loc = summary.get(item.branchId)!;
        loc.itemsCount++;
        loc.totalQuantity += item.totalQuantity;
        loc.totalValue += item.totalValue;
      }
    });

    summary.forEach(loc => {
      loc.avgMovement = loc.totalQuantity / periodFilter;
    });

    return Array.from(summary.values()).sort((a, b) => b.totalQuantity - a.totalQuantity);
  };

  const getStatistics = () => {
    const items = getFastMovingItems();
    const fastest = items.length > 0 ? items[0] : null;
    
    return {
      totalItems: items.length,
      totalMovement: items.reduce((sum, i) => sum + i.totalQuantity, 0),
      totalValue: items.reduce((sum, i) => sum + i.totalValue, 0),
      avgMovement: items.length > 0 ? items.reduce((sum, i) => sum + i.avgDailyMovement, 0) / items.length : 0,
      fastestItem: fastest,
    };
  };

  const toggleRow = (id: number) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedRows(newExpanded);
  };

  const getItemsByCategory = (categoryId?: number): FastMovingItem[] => {
    const items = getFastMovingItems();
    return items.filter(item => item.categoryId === categoryId).sort((a, b) => b.totalQuantity - a.totalQuantity);
  };

  const getItemsByWarehouse = (warehouseId?: number): FastMovingItem[] => {
    const items = getFastMovingItems();
    return items.filter(item => item.warehouseId === warehouseId).sort((a, b) => b.totalQuantity - a.totalQuantity);
  };

  const getItemsByBranch = (branchId?: number): FastMovingItem[] => {
    const items = getFastMovingItems();
    return items.filter(item => item.branchId === branchId).sort((a, b) => b.totalQuantity - a.totalQuantity);
  };

  const formatCurrency = (amount: number) => {
    return amount.toFixed(3);
  };

  const formatNumber = (num: number) => {
    return num.toFixed(2);
  };

  const exportToExcel = async () => {
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet(t.title);

      let currentRow = 1;

      if (companyInfo?.logoBase64) {
        try {
          const logoImage = workbook.addImage({
            base64: companyInfo.logoBase64,
            extension: 'png',
          });
          worksheet.addImage(logoImage, {
            tl: { col: 0, row: 0 },
            ext: { width: 80, height: 60 }
          });
        } catch (err) {
          console.warn('Could not add logo:', err);
        }
      }

      worksheet.mergeCells('B1:H1');
      const companyCell = worksheet.getCell('B1');
      companyCell.value = isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName;
      companyCell.font = { size: 18, bold: true, color: { argb: 'FFFF6B35' } };
      companyCell.alignment = { horizontal: 'center', vertical: 'middle' };

      worksheet.mergeCells('B2:H2');
      const addressCell = worksheet.getCell('B2');
      addressCell.value = `📍 ${companyInfo?.address || ''}`;
      addressCell.font = { size: 9, color: { argb: 'FF424242' } };
      addressCell.alignment = { horizontal: 'center' };

      worksheet.mergeCells('B3:H3');
      const contactCell = worksheet.getCell('B3');
      contactCell.value = `📞 ${companyInfo?.phone || ''} | ✉️ ${companyInfo?.email || ''}`;
      contactCell.font = { size: 9, color: { argb: 'FF424242' } };
      contactCell.alignment = { horizontal: 'center' };

      currentRow = 5;

      worksheet.mergeCells(`A${currentRow}:H${currentRow}`);
      const titleCell = worksheet.getCell(`A${currentRow}`);
      titleCell.value = isRTL ? t.titleAr : t.title;
      titleCell.font = { size: 14, bold: true, color: { argb: 'FFFFFFFF' } };
      titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
      titleCell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FFFF6B35' }
      };

      currentRow++;

      const stats = getStatistics();
      worksheet.mergeCells(`A${currentRow}:H${currentRow}`);
      const statsCell = worksheet.getCell(`A${currentRow}`);
      statsCell.value = `${t.period}: ${periodFilter} ${t.itemsCount} | ${t.totalMovement}: ${formatNumber(stats.totalMovement)}`;
      statsCell.font = { size: 10, bold: true, color: { argb: 'FFFF6B35' } };
      statsCell.alignment = { horizontal: 'center' };

      currentRow++;

      let filterText = '';
      if (warehouseFilter) {
        const wh = warehouses.find(w => w.warehouseId === warehouseFilter);
        filterText += `${t.warehouse}: ${isRTL ? wh?.warehouseNameAr : wh?.warehouseName}`;
      }
      if (branchFilter) {
        const br = branches.find(b => b.branchId === branchFilter);
        if (filterText) filterText += ' | ';
        filterText += `${t.branch}: ${isRTL ? br?.branchNameAr : br?.branchName}`;
      }

      if (filterText) {
        worksheet.mergeCells(`A${currentRow}:H${currentRow}`);
        const filterCell = worksheet.getCell(`A${currentRow}`);
        filterCell.value = `🔍 ${filterText}`;
        filterCell.font = { size: 9, color: { argb: 'FFFF6B35' } };
        filterCell.alignment = { horizontal: 'center' };
        currentRow++;
      }

      currentRow++;

      const headerRow = worksheet.addRow([
        '#',
        t.itemCode,
        t.itemName,
        t.categoryName,
        t.movementQty,
        t.avgMovement,
        t.movementValue,
        t.movementRate
      ]);

      headerRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 10 };
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFFF6B35' }
        };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' }
        };
      });

      const dataItems = getTop20Items();
      dataItems.forEach((item, index) => {
        const row = worksheet.addRow([
          index + 1,
          item.itemCode,
          isRTL ? item.itemNameAr : item.itemName,
          isRTL ? item.categoryNameAr : item.categoryName,
          formatNumber(item.totalQuantity),
          formatNumber(item.avgDailyMovement),
          formatCurrency(item.totalValue),
          formatNumber(item.movementRate)
        ]);

        row.eachCell((cell, colNumber) => {
          cell.alignment = { horizontal: colNumber === 1 ? 'center' : (colNumber > 4 ? 'right' : 'left'), vertical: 'middle' };
          cell.border = {
            top: { style: 'thin' },
            left: { style: 'thin' },
            bottom: { style: 'thin' },
            right: { style: 'thin' }
          };
          if (index % 2 === 1) {
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: { argb: 'FFF5F5F5' }
            };
          }
        });
      });

      worksheet.columns = [
        { width: 8 },
        { width: 15 },
        { width: 35 },
        { width: 20 },
        { width: 15 },
        { width: 18 },
        { width: 18 },
        { width: 18 }
      ];

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${t.title}_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);

      toast.success(t.exportExcel + ' ✓');
    } catch (error) {
      console.error('Export failed:', error);
      toast.error(isRTL ? 'فشل التصدير' : 'Export failed');
    }
  };

  const exportToPdf = async () => {
    try {
      const doc = new jsPDF('l', 'mm', 'a4');
      
      let yPosition = 20;

      if (companyInfo?.logoBase64) {
        try {
          doc.addImage(companyInfo.logoBase64, 'PNG', 10, 10, 30, 20);
        } catch (err) {
          console.warn('Could not add logo to PDF:', err);
        }
      }

      doc.setFontSize(18);
      doc.setTextColor('#FF6B35');
      doc.text(companyInfo?.companyName || '', doc.internal.pageSize.getWidth() / 2, 15, { align: 'center' });

      doc.setFontSize(8);
      doc.setTextColor('#424242');
      doc.text(companyInfo?.address || '', doc.internal.pageSize.getWidth() / 2, 21, { align: 'center' });
      doc.text(`${companyInfo?.phone || ''} | ${companyInfo?.email || ''}`, doc.internal.pageSize.getWidth() / 2, 26, { align: 'center' });

      yPosition = 35;

      doc.setFillColor(255, 107, 53);
      doc.rect(10, yPosition, doc.internal.pageSize.getWidth() - 20, 10, 'F');
      doc.setFontSize(14);
      doc.setTextColor('#FFFFFF');
      doc.text(t.title, doc.internal.pageSize.getWidth() / 2, yPosition + 7, { align: 'center' });

      yPosition += 15;

      const stats = getStatistics();
      doc.setFontSize(9);
      doc.setTextColor('#FF6B35');
      doc.text(`${t.period}: ${periodFilter} days | ${t.totalMovement}: ${formatNumber(stats.totalMovement)}`, doc.internal.pageSize.getWidth() / 2, yPosition, { align: 'center' });

      yPosition += 8;

      const headers = [['#', t.itemCode, t.itemName, t.categoryName, t.movementQty, t.avgMovement, t.movementValue, t.movementRate]];
      const data = getTop20Items().map((item, index) => [
        index + 1,
        item.itemCode,
        isRTL ? item.itemNameAr : item.itemName,
        isRTL ? item.categoryNameAr : item.categoryName,
        formatNumber(item.totalQuantity),
        formatNumber(item.avgDailyMovement),
        formatCurrency(item.totalValue),
        formatNumber(item.movementRate)
      ]);

      autoTable(doc, {
        head: headers,
        body: data,
        startY: yPosition,
        theme: 'grid',
        headStyles: {
          fillColor: [255, 107, 53],
          textColor: [255, 255, 255],
          fontSize: 8,
          fontStyle: 'bold',
          halign: 'center'
        },
        bodyStyles: {
          fontSize: 7,
          textColor: [0, 0, 0]
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245]
        },
        columnStyles: {
          0: { halign: 'center', cellWidth: 10 },
          1: { halign: 'left', cellWidth: 25 },
          2: { halign: 'left', cellWidth: 60 },
          3: { halign: 'left', cellWidth: 35 },
          4: { halign: 'right', cellWidth: 25 },
          5: { halign: 'right', cellWidth: 30 },
          6: { halign: 'right', cellWidth: 30 },
          7: { halign: 'right', cellWidth: 30 }
        },
        margin: { left: 10, right: 10 }
      });

      doc.save(`${t.title}_${new Date().toISOString().split('T')[0]}.pdf`);

      toast.success(t.exportPdf + ' ✓');
    } catch (error) {
      console.error('PDF export failed:', error);
      toast.error(isRTL ? 'فشل تصدير PDF' : 'PDF export failed');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const stats = getStatistics();

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }} className="no-print">
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#FF6B35' }}>
          {isRTL ? t.titleAr : t.title}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title={t.refresh}>
            <IconButton onClick={loadData} sx={{ color: '#FF6B35' }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t.exportExcel}>
            <IconButton onClick={exportToExcel} sx={{ color: '#4CAF50' }}>
              <ExcelIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t.exportPdf}>
            <IconButton onClick={exportToPdf} sx={{ color: '#F44336' }}>
              <PdfIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t.print}>
            <IconButton onClick={handlePrint} sx={{ color: '#2196F3' }}>
              <PrintIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' }, gap: 2, mb: 3 }} className="no-print">
        <Card sx={{ background: 'linear-gradient(135deg, #FF6B35 0%, #FF8C42 100%)', color: 'white', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>{t.totalMovement}</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>{formatNumber(stats.totalMovement)}</Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>{t.itemsCount}</Typography>
              </Box>
              <TrendingUp sx={{ fontSize: 48, opacity: 0.3 }} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ background: 'linear-gradient(135deg, #66BB6A 0%, #81C784 100%)', color: 'white', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>{t.fastestItem}</Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold', mt: 1 }}>
                  {stats.fastestItem ? (isRTL ? stats.fastestItem.itemNameAr : stats.fastestItem.itemName).substring(0, 15) : '-'}
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                  {stats.fastestItem ? formatNumber(stats.fastestItem.avgDailyMovement) + ' ' + t.unitsPerDay : ''}
                </Typography>
              </Box>
              <Speed sx={{ fontSize: 48, opacity: 0.3 }} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ background: 'linear-gradient(135deg, #FFA726 0%, #FFB74D 100%)', color: 'white', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>{t.movementValue}</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>{formatCurrency(stats.totalValue)}</Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>{t.kwd}</Typography>
              </Box>
              <LocalShipping sx={{ fontSize: 48, opacity: 0.3 }} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ background: 'linear-gradient(135deg, #8E24AA 0%, #BA68C8 100%)', color: 'white', transition: 'transform 0.3s', '&:hover': { transform: 'translateY(-5px)' } }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>{t.totalItems}</Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold', mt: 1 }}>{stats.totalItems}</Typography>
                <Typography variant="caption" sx={{ opacity: 0.8 }}>{t.itemsCount}</Typography>
              </Box>
              <CategoryIcon sx={{ fontSize: 48, opacity: 0.3 }} />
            </Box>
          </CardContent>
        </Card>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }} className="no-print">
        <Typography variant="h6" sx={{ mb: 2, color: '#FF6B35' }}>Filters</Typography>
        
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2, mb: 2 }}>
          <Autocomplete
            size="small"
            options={[{ id: '' as number | '', label: t.allWarehouses }, ...warehouses.map(wh => ({ id: wh.warehouseId as number | '', label: isRTL ? wh.warehouseNameAr || wh.warehouseName : wh.warehouseName }))]}
            getOptionLabel={(o) => o.label || ''}
            value={[{ id: '' as number | '', label: t.allWarehouses }, ...warehouses.map(wh => ({ id: wh.warehouseId as number | '', label: isRTL ? wh.warehouseNameAr || wh.warehouseName : wh.warehouseName }))].find(o => o.id === warehouseFilter) || { id: '' as number | '', label: t.allWarehouses }}
            onChange={(_, v) => setWarehouseFilter(v?.id || '')}
            renderInput={(params) => <TextField {...params} label={t.warehouse} />}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            fullWidth
          />

          <Autocomplete
            size="small"
            options={[{ id: '' as number | '', label: t.allBranches }, ...branches.map(br => ({ id: br.branchId as number | '', label: isRTL ? br.branchNameAr || br.branchName : br.branchName }))]}
            getOptionLabel={(o) => o.label || ''}
            value={[{ id: '' as number | '', label: t.allBranches }, ...branches.map(br => ({ id: br.branchId as number | '', label: isRTL ? br.branchNameAr || br.branchName : br.branchName }))].find(o => o.id === branchFilter) || { id: '' as number | '', label: t.allBranches }}
            onChange={(_, v) => setBranchFilter(v?.id || '')}
            renderInput={(params) => <TextField {...params} label={t.branch} />}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            fullWidth
          />

          <TextField select fullWidth size="small" label={t.period} value={periodFilter}
            onChange={(e) => setPeriodFilter(Number(e.target.value))}>
            <MenuItem value={30}>{t.lastMonth}</MenuItem>
            <MenuItem value={90}>{t.last3Months}</MenuItem>
            <MenuItem value={180}>{t.last6Months}</MenuItem>
            <MenuItem value={365}>{t.lastYear}</MenuItem>
          </TextField>
        </Box>

        <Button variant="contained" onClick={loadData} fullWidth
          sx={{ background: 'linear-gradient(45deg, #FF6B35 30%, #FF8C42 90%)', color: 'white' }}>
          {t.refresh}
        </Button>
      </Paper>

      {(warehouseFilter || branchFilter) && (
        <Paper sx={{ p: 2, mb: 2, background: 'linear-gradient(135deg, #FFE0E0 0%, #FFCCCC 100%)', border: '2px solid #FF6B35' }} className="no-print">
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: '#FF6B35', mb: 1 }}>
            🔍 {t.filters}:
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {warehouseFilter && (
              <Chip 
                label={`${t.warehouse}: ${warehouses.find(w => w.warehouseId === warehouseFilter)?.[isRTL ? 'warehouseNameAr' : 'warehouseName']}`}
                sx={{ background: '#FF6B35', color: 'white', fontWeight: 'bold' }}
              />
            )}
            {branchFilter && (
              <Chip 
                label={`${t.branch}: ${branches.find(b => b.branchId === branchFilter)?.[isRTL ? 'branchNameAr' : 'branchName']}`}
                sx={{ background: '#F44336', color: 'white', fontWeight: 'bold' }}
              />
            )}
          </Box>
        </Paper>
      )}

      <Paper sx={{ mb: 2 }} className="no-print">
        <Tabs value={currentTab} onChange={(_, newValue) => setCurrentTab(newValue)} variant="scrollable" scrollButtons="auto">
          <Tab label={t.top20} />
          <Tab label={t.byCategory} />
          <Tab label={t.byWarehouse} />
          <Tab label={t.byBranch} />
        </Tabs>
      </Paper>

      <Box className="print-only">
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, pb: 2, borderBottom: '3px solid #FF6B35' }}>
          {companyInfo?.logoBase64 && (
            <Box sx={{ width: '100px' }}>
              <img src={companyInfo.logoBase64} alt="Logo" style={{ height: '60px', maxWidth: '100px', objectFit: 'contain' }} />
            </Box>
          )}
          <Box sx={{ textAlign: 'center', flex: 1 }}>
            <Typography sx={{ fontSize: '22px', fontWeight: 'bold', color: '#FF6B35', mb: 0.5 }}>
              {isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName}
            </Typography>
            <Typography sx={{ fontSize: '10px', color: '#424242', mb: 0.3 }}>
              📍 {companyInfo?.address || ''}
            </Typography>
            <Typography sx={{ fontSize: '10px', color: '#424242' }}>
              📞 {companyInfo?.phone || ''} | 📧 {companyInfo?.email || ''}
            </Typography>
          </Box>
          <Box sx={{ width: '100px' }}></Box>
        </Box>

        <Box sx={{ textAlign: 'center', mb: 2, py: 1, background: '#FF6B35', color: 'white', borderRadius: '4px' }}>
          <Typography sx={{ fontSize: '16px', fontWeight: 'bold' }}>
            {isRTL ? t.titleAr : t.title}
          </Typography>
          <Typography sx={{ fontSize: '10px', mt: 0.5 }}>
            {t.period}: {periodFilter} days
          </Typography>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2, p: 2, background: '#F5F5F5', border: '2px solid #FF6B35', borderRadius: '4px' }}>
          <Box>
            <Typography sx={{ fontSize: '11px', fontWeight: 'bold', color: '#FF6B35', mb: 1 }}>
              📊 Statistics:
            </Typography>
            <Typography sx={{ fontSize: '10px', color: '#000000' }}>
              • {t.totalMovement}: {formatNumber(stats.totalMovement)}
            </Typography>
            <Typography sx={{ fontSize: '10px', color: '#000000' }}>
              • {t.movementValue}: {formatCurrency(stats.totalValue)} {t.kwd}
            </Typography>
          </Box>
          
          <Box>
            <Typography sx={{ fontSize: '11px', fontWeight: 'bold', color: '#FF6B35', mb: 1 }}>
              🔍 {t.filters}:
            </Typography>
            {warehouseFilter && (
              <Typography sx={{ fontSize: '10px', color: '#000000' }}>
                • {t.warehouse}: {warehouses.find(w => w.warehouseId === warehouseFilter)?.[isRTL ? 'warehouseNameAr' : 'warehouseName']}
              </Typography>
            )}
            {branchFilter && (
              <Typography sx={{ fontSize: '10px', color: '#000000' }}>
                • {t.branch}: {branches.find(b => b.branchId === branchFilter)?.[isRTL ? 'branchNameAr' : 'branchName']}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress sx={{ color: '#FF6B35' }} /></Box>
      ) : (
        <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
          {currentTab === 0 && (
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ backgroundColor: '#FF6B35', color: '#FFFFFF', fontWeight: 'bold', fontSize: '0.9rem' }}>{t.itemName}</TableCell>
                  <TableCell sx={{ backgroundColor: '#FF6B35', color: '#FFFFFF', fontWeight: 'bold', fontSize: '0.9rem' }}>{t.categoryName}</TableCell>
                  <TableCell align="right" sx={{ backgroundColor: '#FF6B35', color: '#FFFFFF', fontWeight: 'bold', fontSize: '0.9rem' }}>{t.movementQty}</TableCell>
                  <TableCell align="right" sx={{ backgroundColor: '#FF6B35', color: '#FFFFFF', fontWeight: 'bold', fontSize: '0.9rem' }}>{t.avgMovement}</TableCell>
                  <TableCell align="right" sx={{ backgroundColor: '#FF6B35', color: '#FFFFFF', fontWeight: 'bold', fontSize: '0.9rem' }}>{t.movementValue}</TableCell>
                  <TableCell align="center" sx={{ backgroundColor: '#FF6B35', color: '#FFFFFF', fontWeight: 'bold', fontSize: '0.9rem' }}>{t.movementRate}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getTop20Items().map((row, index) => {
                  const maxRate = Math.max(...getTop20Items().map(i => i.movementRate));
                  return (
                    <TableRow key={index} sx={{ 
                      '&:hover': { backgroundColor: '#FFECB3' },
                      backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#F5F5F5'
                    }}>
                      <TableCell sx={{ borderBottom: '1px solid #E0E0E0', color: '#212121' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Chip 
                            label={index + 1} 
                            size="small" 
                            sx={{ 
                              background: index < 3 ? (
                                index === 0 ? '#FFD700' : index === 1 ? '#C0C0C0' : '#CD7F32'
                              ) : '#424242',
                              color: index < 3 ? '#000000' : '#FFFFFF',
                              fontWeight: 'bold',
                              minWidth: 38,
                              height: 28,
                              fontSize: '0.85rem'
                            }} 
                          />
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: '600', fontSize: '0.95rem', color: '#212121' }}>
                              {isRTL ? row.itemNameAr : row.itemName}
                            </Typography>
                            <Typography variant="caption" sx={{ color: '#757575', fontSize: '0.75rem' }}>
                              {row.itemCode}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ borderBottom: '1px solid #E0E0E0' }}>
                        <Chip 
                          label={isRTL ? row.categoryNameAr : row.categoryName} 
                          size="small" 
                          sx={{ 
                            backgroundColor: '#E8F5E9', 
                            color: '#1B5E20',
                            fontWeight: '600',
                            fontSize: '0.8rem'
                          }} 
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', color: '#D84315', fontSize: '0.95rem', borderBottom: '1px solid #E0E0E0' }}>
                        {formatNumber(row.totalQuantity)}
                      </TableCell>
                      <TableCell align="right" sx={{ color: '#424242', fontSize: '0.9rem', borderBottom: '1px solid #E0E0E0' }}>
                        {formatNumber(row.avgDailyMovement)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 'bold', color: '#1565C0', fontSize: '0.95rem', borderBottom: '1px solid #E0E0E0' }}>
                        {formatCurrency(row.totalValue)}
                      </TableCell>
                      <TableCell sx={{ borderBottom: '1px solid #E0E0E0' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LinearProgress 
                            variant="determinate" 
                            value={(row.movementRate / maxRate) * 100} 
                            sx={{ 
                              flex: 1, 
                              height: 10, 
                              borderRadius: 5,
                              backgroundColor: '#FFE0B2',
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: '#FF6F00'
                              }
                            }} 
                          />
                          <Typography variant="body2" sx={{ minWidth: 70, fontWeight: '600', color: '#424242', textAlign: 'right' }}>
                            {formatNumber(row.movementRate)} {t.unitsPerDay}
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {getTop20Items().length === 0 && (
                  <TableRow><TableCell colSpan={6} align="center" sx={{ py: 5 }}><Typography color="text.secondary">{t.noData}</Typography></TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          )}

          {currentTab === 1 && (
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ backgroundColor: '#1B5E20', color: '#FFFFFF', fontWeight: 'bold', fontSize: '0.9rem', width: 50 }}></TableCell>
                  <TableCell sx={{ backgroundColor: '#1B5E20', color: '#FFFFFF', fontWeight: 'bold', fontSize: '0.9rem' }}>{t.categoryName}</TableCell>
                  <TableCell align="right" sx={{ backgroundColor: '#1B5E20', color: '#FFFFFF', fontWeight: 'bold', fontSize: '0.9rem' }}>{t.totalItems}</TableCell>
                  <TableCell align="right" sx={{ backgroundColor: '#1B5E20', color: '#FFFFFF', fontWeight: 'bold', fontSize: '0.9rem' }}>{t.movementQty}</TableCell>
                  <TableCell align="right" sx={{ backgroundColor: '#1B5E20', color: '#FFFFFF', fontWeight: 'bold', fontSize: '0.9rem' }}>{t.avgMovement}</TableCell>
                  <TableCell align="right" sx={{ backgroundColor: '#1B5E20', color: '#FFFFFF', fontWeight: 'bold', fontSize: '0.9rem' }}>{t.movementValue}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getCategorySummary().map((row, index) => {
                  const categoryItems = getItemsByCategory(row.categoryId);
                  const isExpanded = expandedRows.has(row.categoryId || 0);
                  
                  return (
                    <React.Fragment key={index}>
                      <TableRow sx={{ 
                        '&:hover': { backgroundColor: '#E8F5E9' },
                        backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#F5F5F5',
                        cursor: 'pointer'
                      }} onClick={() => toggleRow(row.categoryId || 0)}>
                        <TableCell sx={{ borderBottom: '1px solid #E0E0E0' }}>
                          <IconButton size="small">
                            {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                          </IconButton>
                        </TableCell>
                        <TableCell sx={{ borderBottom: '1px solid #E0E0E0' }}>
                          <Chip 
                            label={isRTL ? row.categoryNameAr : row.categoryName} 
                            sx={{ 
                              backgroundColor: '#C8E6C9', 
                              color: '#1B5E20',
                              fontWeight: '600',
                              border: '2px solid #66BB6A'
                            }} 
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ borderBottom: '1px solid #E0E0E0' }}>
                          <Chip label={row.itemsCount} size="small" sx={{ backgroundColor: '#BBDEFB', color: '#0D47A1', fontWeight: '600' }} />
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: '#D84315', fontSize: '0.95rem', borderBottom: '1px solid #E0E0E0' }}>
                          {formatNumber(row.totalQuantity)}
                        </TableCell>
                        <TableCell align="right" sx={{ color: '#424242', fontSize: '0.9rem', borderBottom: '1px solid #E0E0E0' }}>
                          {formatNumber(row.avgMovement)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: '#1565C0', fontSize: '0.95rem', borderBottom: '1px solid #E0E0E0' }}>
                          {formatCurrency(row.totalValue)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ margin: 2 }}>
                              <Typography variant="h6" gutterBottom component="div" sx={{ color: '#1B5E20', fontSize: '0.9rem', fontWeight: 'bold' }}>
                                {t.itemDetails}
                              </Typography>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={{ backgroundColor: '#E8F5E9', color: '#1B5E20', fontWeight: 'bold' }}>#</TableCell>
                                    <TableCell sx={{ backgroundColor: '#E8F5E9', color: '#1B5E20', fontWeight: 'bold' }}>{t.itemCode}</TableCell>
                                    <TableCell sx={{ backgroundColor: '#E8F5E9', color: '#1B5E20', fontWeight: 'bold' }}>{t.itemName}</TableCell>
                                    <TableCell align="right" sx={{ backgroundColor: '#E8F5E9', color: '#1B5E20', fontWeight: 'bold' }}>{t.movementQty}</TableCell>
                                    <TableCell align="right" sx={{ backgroundColor: '#E8F5E9', color: '#1B5E20', fontWeight: 'bold' }}>{t.movementValue}</TableCell>
                                    <TableCell align="right" sx={{ backgroundColor: '#E8F5E9', color: '#1B5E20', fontWeight: 'bold' }}>{t.movementRate}</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {categoryItems.map((item, idx) => (
                                    <TableRow key={idx} sx={{ backgroundColor: idx % 2 === 0 ? '#FFFFFF' : '#F5F5F5' }}>
                                      <TableCell sx={{ color: '#212121' }}>{idx + 1}</TableCell>
                                      <TableCell sx={{ color: '#212121' }}>{item.itemCode}</TableCell>
                                      <TableCell sx={{ color: '#212121' }}>{isRTL ? item.itemNameAr : item.itemName}</TableCell>
                                      <TableCell align="right" sx={{ color: '#D84315', fontWeight: '600' }}>{formatNumber(item.totalQuantity)}</TableCell>
                                      <TableCell align="right" sx={{ color: '#1565C0', fontWeight: '600' }}>{formatCurrency(item.totalValue)}</TableCell>
                                      <TableCell align="right" sx={{ color: '#424242' }}>{formatNumber(item.movementRate)} {t.unitsPerDay}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          )}

          {currentTab === 2 && (
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ backgroundColor: '#BF360C', color: '#FFFFFF', fontWeight: 'bold', fontSize: '0.9rem', width: 50 }}></TableCell>
                  <TableCell sx={{ backgroundColor: '#BF360C', color: '#FFFFFF', fontWeight: 'bold', fontSize: '0.9rem' }}>{t.warehouseName}</TableCell>
                  <TableCell align="right" sx={{ backgroundColor: '#BF360C', color: '#FFFFFF', fontWeight: 'bold', fontSize: '0.9rem' }}>{t.totalItems}</TableCell>
                  <TableCell align="right" sx={{ backgroundColor: '#BF360C', color: '#FFFFFF', fontWeight: 'bold', fontSize: '0.9rem' }}>{t.movementQty}</TableCell>
                  <TableCell align="right" sx={{ backgroundColor: '#BF360C', color: '#FFFFFF', fontWeight: 'bold', fontSize: '0.9rem' }}>{t.avgMovement}</TableCell>
                  <TableCell align="right" sx={{ backgroundColor: '#BF360C', color: '#FFFFFF', fontWeight: 'bold', fontSize: '0.9rem' }}>{t.movementValue}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getWarehouseSummary().map((row, index) => {
                  const warehouseItems = getItemsByWarehouse(row.locationId);
                  const isExpanded = expandedRows.has(row.locationId || 0);
                  
                  return (
                    <React.Fragment key={index}>
                      <TableRow sx={{ 
                        '&:hover': { backgroundColor: '#FBE9E7' },
                        backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#F5F5F5',
                        cursor: 'pointer'
                      }} onClick={() => toggleRow(row.locationId || 0)}>
                        <TableCell sx={{ borderBottom: '1px solid #E0E0E0' }}>
                          <IconButton size="small">
                            {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                          </IconButton>
                        </TableCell>
                        <TableCell sx={{ borderBottom: '1px solid #E0E0E0' }}>
                          <Chip 
                            label={isRTL ? row.locationNameAr : row.locationName} 
                            sx={{ 
                              backgroundColor: '#FFCCBC', 
                              color: '#BF360C',
                              fontWeight: '600',
                              border: '2px solid #FF7043'
                            }} 
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ borderBottom: '1px solid #E0E0E0' }}>
                          <Chip label={row.itemsCount} size="small" sx={{ backgroundColor: '#BBDEFB', color: '#0D47A1', fontWeight: '600' }} />
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: '#D84315', fontSize: '0.95rem', borderBottom: '1px solid #E0E0E0' }}>
                          {formatNumber(row.totalQuantity)}
                        </TableCell>
                        <TableCell align="right" sx={{ color: '#424242', fontSize: '0.9rem', borderBottom: '1px solid #E0E0E0' }}>
                          {formatNumber(row.avgMovement)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: '#1565C0', fontSize: '0.95rem', borderBottom: '1px solid #E0E0E0' }}>
                          {formatCurrency(row.totalValue)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ margin: 2 }}>
                              <Typography variant="h6" gutterBottom component="div" sx={{ color: '#BF360C', fontSize: '0.9rem', fontWeight: 'bold' }}>
                                {t.itemDetails}
                              </Typography>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={{ backgroundColor: '#FBE9E7', color: '#BF360C', fontWeight: 'bold' }}>#</TableCell>
                                    <TableCell sx={{ backgroundColor: '#FBE9E7', color: '#BF360C', fontWeight: 'bold' }}>{t.itemCode}</TableCell>
                                    <TableCell sx={{ backgroundColor: '#FBE9E7', color: '#BF360C', fontWeight: 'bold' }}>{t.itemName}</TableCell>
                                    <TableCell align="right" sx={{ backgroundColor: '#FBE9E7', color: '#BF360C', fontWeight: 'bold' }}>{t.movementQty}</TableCell>
                                    <TableCell align="right" sx={{ backgroundColor: '#FBE9E7', color: '#BF360C', fontWeight: 'bold' }}>{t.movementValue}</TableCell>
                                    <TableCell align="right" sx={{ backgroundColor: '#FBE9E7', color: '#BF360C', fontWeight: 'bold' }}>{t.movementRate}</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {warehouseItems.map((item, idx) => (
                                    <TableRow key={idx} sx={{ backgroundColor: idx % 2 === 0 ? '#FFFFFF' : '#F5F5F5' }}>
                                      <TableCell sx={{ color: '#212121' }}>{idx + 1}</TableCell>
                                      <TableCell sx={{ color: '#212121' }}>{item.itemCode}</TableCell>
                                      <TableCell sx={{ color: '#212121' }}>{isRTL ? item.itemNameAr : item.itemName}</TableCell>
                                      <TableCell align="right" sx={{ color: '#D84315', fontWeight: '600' }}>{formatNumber(item.totalQuantity)}</TableCell>
                                      <TableCell align="right" sx={{ color: '#1565C0', fontWeight: '600' }}>{formatCurrency(item.totalValue)}</TableCell>
                                      <TableCell align="right" sx={{ color: '#424242' }}>{formatNumber(item.movementRate)} {t.unitsPerDay}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          )}

          {currentTab === 3 && (
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ backgroundColor: '#4A148C', color: '#FFFFFF', fontWeight: 'bold', fontSize: '0.9rem', width: 50 }}></TableCell>
                  <TableCell sx={{ backgroundColor: '#4A148C', color: '#FFFFFF', fontWeight: 'bold', fontSize: '0.9rem' }}>{t.branchName}</TableCell>
                  <TableCell align="right" sx={{ backgroundColor: '#4A148C', color: '#FFFFFF', fontWeight: 'bold', fontSize: '0.9rem' }}>{t.totalItems}</TableCell>
                  <TableCell align="right" sx={{ backgroundColor: '#4A148C', color: '#FFFFFF', fontWeight: 'bold', fontSize: '0.9rem' }}>{t.movementQty}</TableCell>
                  <TableCell align="right" sx={{ backgroundColor: '#4A148C', color: '#FFFFFF', fontWeight: 'bold', fontSize: '0.9rem' }}>{t.avgMovement}</TableCell>
                  <TableCell align="right" sx={{ backgroundColor: '#4A148C', color: '#FFFFFF', fontWeight: 'bold', fontSize: '0.9rem' }}>{t.movementValue}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getBranchSummary().map((row, index) => {
                  const branchItems = getItemsByBranch(row.locationId);
                  const isExpanded = expandedRows.has(row.locationId || 0);
                  
                  return (
                    <React.Fragment key={index}>
                      <TableRow sx={{ 
                        '&:hover': { backgroundColor: '#F3E5F5' },
                        backgroundColor: index % 2 === 0 ? '#FFFFFF' : '#F5F5F5',
                        cursor: 'pointer'
                      }} onClick={() => toggleRow(row.locationId || 0)}>
                        <TableCell sx={{ borderBottom: '1px solid #E0E0E0' }}>
                          <IconButton size="small">
                            {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                          </IconButton>
                        </TableCell>
                        <TableCell sx={{ borderBottom: '1px solid #E0E0E0' }}>
                          <Chip 
                            label={isRTL ? row.locationNameAr : row.locationName} 
                            sx={{ 
                              backgroundColor: '#E1BEE7', 
                              color: '#4A148C',
                              fontWeight: '600',
                              border: '2px solid #AB47BC'
                            }} 
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ borderBottom: '1px solid #E0E0E0' }}>
                          <Chip label={row.itemsCount} size="small" sx={{ backgroundColor: '#BBDEFB', color: '#0D47A1', fontWeight: '600' }} />
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: '#D84315', fontSize: '0.95rem', borderBottom: '1px solid #E0E0E0' }}>
                          {formatNumber(row.totalQuantity)}
                        </TableCell>
                        <TableCell align="right" sx={{ color: '#424242', fontSize: '0.9rem', borderBottom: '1px solid #E0E0E0' }}>
                          {formatNumber(row.avgMovement)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: '#1565C0', fontSize: '0.95rem', borderBottom: '1px solid #E0E0E0' }}>
                          {formatCurrency(row.totalValue)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6}>
                          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ margin: 2 }}>
                              <Typography variant="h6" gutterBottom component="div" sx={{ color: '#4A148C', fontSize: '0.9rem', fontWeight: 'bold' }}>
                                {t.itemDetails}
                              </Typography>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell sx={{ backgroundColor: '#F3E5F5', color: '#4A148C', fontWeight: 'bold' }}>#</TableCell>
                                    <TableCell sx={{ backgroundColor: '#F3E5F5', color: '#4A148C', fontWeight: 'bold' }}>{t.itemCode}</TableCell>
                                    <TableCell sx={{ backgroundColor: '#F3E5F5', color: '#4A148C', fontWeight: 'bold' }}>{t.itemName}</TableCell>
                                    <TableCell align="right" sx={{ backgroundColor: '#F3E5F5', color: '#4A148C', fontWeight: 'bold' }}>{t.movementQty}</TableCell>
                                    <TableCell align="right" sx={{ backgroundColor: '#F3E5F5', color: '#4A148C', fontWeight: 'bold' }}>{t.movementValue}</TableCell>
                                    <TableCell align="right" sx={{ backgroundColor: '#F3E5F5', color: '#4A148C', fontWeight: 'bold' }}>{t.movementRate}</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {branchItems.map((item, idx) => (
                                    <TableRow key={idx} sx={{ backgroundColor: idx % 2 === 0 ? '#FFFFFF' : '#F5F5F5' }}>
                                      <TableCell sx={{ color: '#212121' }}>{idx + 1}</TableCell>
                                      <TableCell sx={{ color: '#212121' }}>{item.itemCode}</TableCell>
                                      <TableCell sx={{ color: '#212121' }}>{isRTL ? item.itemNameAr : item.itemName}</TableCell>
                                      <TableCell align="right" sx={{ color: '#D84315', fontWeight: '600' }}>{formatNumber(item.totalQuantity)}</TableCell>
                                      <TableCell align="right" sx={{ color: '#1565C0', fontWeight: '600' }}>{formatCurrency(item.totalValue)}</TableCell>
                                      <TableCell align="right" sx={{ color: '#424242' }}>{formatNumber(item.movementRate)} {t.unitsPerDay}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </TableContainer>
      )}

      <style>{`
        @media print {
          header, nav, .MuiAppBar-root, .MuiDrawer-root, .MuiToolbar-root { display: none !important; }
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          @page { margin: 1.5cm 1cm; size: landscape; }
          * { orphans: 3; widows: 3; }
          body { font-size: 10px; color: #000000 !important; background: white !important; }
          main, .MuiBox-root { margin: 0 !important; padding: 10px !important; max-width: 100% !important; }
          table { page-break-after: avoid; page-break-inside: auto; border-collapse: collapse !important; width: 100%; }
          table tr { page-break-inside: avoid; page-break-after: auto; }
          table thead tr { background: #FFE0D5 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          table thead th { background: #FFE0D5 !important; color: #000000 !important; font-weight: bold !important; padding: 8px 6px !important; border: 2px solid #FF6B35 !important; font-size: 10px !important; text-align: center !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          table tbody tr { background: white !important; }
          table tbody tr:nth-child(even) { background: #F5F5F5 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          table tbody td { padding: 6px 8px !important; border: 1px solid #BDBDBD !important; color: #000000 !important; font-size: 9px !important; line-height: 1.4 !important; }
          table tbody td .MuiTypography-caption { color: #424242 !important; opacity: 1 !important; font-size: 8px !important; }
          table tbody td[align="right"] { font-weight: 600 !important; }
          .MuiChip-root { height: auto !important; padding: 2px 8px !important; font-size: 8px !important; border: 1px solid #000000 !important; background: white !important; color: #000000 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .MuiTableContainer-root { box-shadow: none !important; border-radius: 0 !important; overflow: visible !important; border: 2px solid #FF6B35 !important; }
          .print-only h4, .print-only h3, .print-only h5 { color: #000000 !important; font-weight: bold !important; }
          .print-only .MuiPaper-root { box-shadow: none !important; background: white !important; }
          .print-only img { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .print-only > div:nth-child(2) { background: #FF6B35 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; padding: 8px !important; }
          .print-only > div:nth-child(2) > div { color: white !important; }
          .print-only > div:first-child { border-bottom: 3px solid #FF6B35 !important; padding-bottom: 10px !important; margin-bottom: 10px !important; }
          .print-only > div:nth-child(3) { border: 2px solid #FF6B35 !important; padding: 8px !important; margin-bottom: 10px !important; background: #F5F5F5 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .MuiLinearProgress-root { display: none !important; }
          .MuiCollapse-root { display: none !important; }
          .MuiIconButton-root { display: none !important; }
        }
        @media screen { .print-only { display: none !important; } }
      `}</style>
    </Box>
  );
};

export default FastMovingReport;